import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';

import {
  Button,
  ButtonGroup,
  CircularProgress,
  ClickAwayListener,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grow,
  MenuItem,
  MenuList,
  Paper,
  Popper
} from '@mui/material';

const SUBMISSION_ACTIONS = [
  { value: 'all', label: 'Proceed with all the items of selected invoice' },
  { value: 'selected', label: 'Proceed with only selected' }
];

const InvoiceDialog = ({
  partialSelectionDialogOpen,
  setPartialSelectionDialogOpen,
  bulkAction,
  partiallySelectedInvoices,
  submitWithMode
}) => {
  const [selectedAction, setSelectedAction] = useState(SUBMISSION_ACTIONS[0].value);
  const [menuOpen, setMenuOpen] = useState(false);
  const anchorRef = useRef(null);
  const selectedOption = SUBMISSION_ACTIONS.find(({ value }) => value === selectedAction) || SUBMISSION_ACTIONS[0];

  useEffect(() => {
    if (!partialSelectionDialogOpen) return;
    setSelectedAction(SUBMISSION_ACTIONS[0].value);
    setMenuOpen(false);
  }, [partialSelectionDialogOpen]);

  const closeMenu = (event) => {
    if (anchorRef.current?.contains(event.target)) return;
    setMenuOpen(false);
  };

  return (
    <React.Fragment>
      <Dialog
        open={partialSelectionDialogOpen}
        onClose={() => {
          if (!bulkAction) setPartialSelectionDialogOpen(false);
        }}
        aria-labelledby="partial-invoice-selection-title"
      >
        <DialogTitle id="partial-invoice-selection-title">Some invoice items are not selected</DialogTitle>
        <DialogContent>
          <DialogContentText>
            The following invoices still have line items that are not selected. Choose which items should be included in
            each QuickBooks supplier bill.
          </DialogContentText>
          <ul className="tw-mt-3 tw-mb-0 tw-pl-5">
            {partiallySelectedInvoices.map(({ invoiceId, invoiceNumber, selectedLineIndexes, totalLineItems }) => (
              <li key={invoiceId}>
                {invoiceNumber}: {selectedLineIndexes.length} of {totalLineItems} items selected
              </li>
            ))}
          </ul>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'space-between' }}>
          <Button onClick={() => setPartialSelectionDialogOpen(false)} disabled={Boolean(bulkAction)}>
            Cancel
          </Button>
          <ButtonGroup
            ref={anchorRef}
            variant="contained"
            size="small"
            aria-label="Choose which invoice items to submit"
          >
            <Button disabled={Boolean(bulkAction)} onClick={() => submitWithMode(selectedOption.value)}>
              {bulkAction === 'submit' ? (
                <React.Fragment>
                  <CircularProgress size={16} color="inherit" className="tw-mr-2" />
                  Submitting…
                </React.Fragment>
              ) : (
                selectedOption.label
              )}
            </Button>
            <Button
              size="small"
              aria-controls={menuOpen ? 'invoice-submission-mode-menu' : undefined}
              aria-expanded={menuOpen ? 'true' : undefined}
              aria-haspopup="menu"
              aria-label="Choose invoice submission mode"
              disabled={Boolean(bulkAction)}
              onClick={() => setMenuOpen((current) => !current)}
              className="tw-z-50"
            >
              <ArrowDropDownIcon />
            </Button>
          </ButtonGroup>
          <Popper
            open={menuOpen}
            anchorEl={anchorRef.current}
            placement="bottom-end"
            transition
            style={{ zIndex: 1400 }}
          >
            {({ TransitionProps }) => (
              <Grow {...TransitionProps}>
                <Paper>
                  <ClickAwayListener onClickAway={closeMenu}>
                    <MenuList id="invoice-submission-mode-menu" autoFocusItem>
                      {SUBMISSION_ACTIONS.filter(({ value }) => value !== selectedOption.value).map((action) => (
                        <MenuItem
                          key={action.value}
                          onClick={() => {
                            setSelectedAction(action.value);
                            setMenuOpen(false);
                          }}
                        >
                          {action.label}
                        </MenuItem>
                      ))}
                    </MenuList>
                  </ClickAwayListener>
                </Paper>
              </Grow>
            )}
          </Popper>
        </DialogActions>
      </Dialog>
    </React.Fragment>
  );
};

export default InvoiceDialog;

InvoiceDialog.propTypes = {
  partialSelectionDialogOpen: PropTypes.any,
  setPartialSelectionDialogOpen: PropTypes.any,
  bulkAction: PropTypes.any,
  partiallySelectedInvoices: PropTypes.any,
  submitWithMode: PropTypes.any
};
